import type * as React from 'react';

import { cn } from '../../styles/lib/utils';
import { type ColorSwatchGroup, ColorSwatchPicker, swatchVariants } from '../ColorSwatchPicker';
import { Input } from '../Input';
import { Text } from '../Text';

interface CategoryColorRow {
  /** Stable key the caller keys colors on, and the value passed back to `onColorChange`. */
  value: string;
  label: string;
  count: number;
  color: string;
}

interface CategoryColorTableProps extends Omit<React.ComponentProps<'div'>, 'onChange' | 'children'> {
  rows: readonly CategoryColorRow[];
  groups: readonly ColorSwatchGroup[];
  /** Renders each row's color as a picker; omit it to keep the swatches static. */
  onColorChange?: (value: string, color: string) => void;
  /** Renders each row's name as an editable input; omit it to keep the names static text. */
  onNameChange?: (value: string, label: string) => void;
  /**
   * Each row's picker gets `${testIdPrefix}-color-${value}` (`${testIdPrefix}-color-static-${value}`
   * when it isn't editable), its swatches `${testIdPrefix}-swatch-${value}-${color}`, and its name
   * input `${testIdPrefix}-name-${value}`.
   */
  testIdPrefix: string;
  /** Lets each row reach any color, not just the ones in `groups`. */
  allowCustomColor?: boolean;
  countLabel?: string;
}

/** A scrollable color/category/count table with one color swatch per row. */
function CategoryColorTable({
  rows,
  onColorChange,
  onNameChange,
  groups,
  testIdPrefix,
  allowCustomColor,
  countLabel = 'Features',
  className,
  ...props
}: CategoryColorTableProps) {
  return (
    <div data-slot="category-color-table" className={cn('rounded-[8px] border border-grey-30', className)} {...props}>
      <div className="flex items-center gap-3 border-grey-20 border-b px-3 py-2">
        <Text className="w-14 font-medium text-grey-50 text-xs">Color</Text>
        <Text className="min-w-0 flex-1 font-medium text-grey-50 text-xs">Category</Text>
        <Text className="w-16 text-right font-medium text-grey-50 text-xs">{countLabel}</Text>
      </div>
      <div className="flex max-h-64 flex-col overflow-y-auto">
        {rows.map(({ value, label, count, color }) => (
          <div key={value} className="flex items-center gap-3 px-3 py-2">
            <div className="w-14">
              {onColorChange ? (
                <ColorSwatchPicker
                  size="small"
                  label={label}
                  value={color}
                  onChange={(next) => onColorChange(value, next)}
                  groups={groups}
                  allowCustomColor={allowCustomColor}
                  data-testid={`${testIdPrefix}-color-${value}`}
                  swatchTestIdPrefix={`${testIdPrefix}-swatch-${value}`}
                />
              ) : (
                <span
                  className={cn(swatchVariants({ size: 'small' }), 'block cursor-default')}
                  style={{ backgroundColor: color }}
                  title={label}
                  data-testid={`${testIdPrefix}-color-static-${value}`}
                />
              )}
            </div>
            {onNameChange ? (
              <Input
                value={label}
                onChange={(event) => onNameChange(value, event.target.value)}
                aria-label={`Category name for ${value || 'uncategorized'}`}
                className="min-w-0 flex-1 text-sm"
                data-testid={`${testIdPrefix}-name-${value}`}
              />
            ) : (
              <Text className="min-w-0 flex-1 truncate text-primary text-sm">{label}</Text>
            )}
            <Text className="w-16 text-right text-grey-50 text-sm">{count}</Text>
          </div>
        ))}
      </div>
    </div>
  );
}

export { CategoryColorTable, type CategoryColorRow };
